import { HiddenContent } from "components/HiddenContent/HiddenContent";
import { TextTruncate } from "../../../../components";
import { PublishStatus } from "components/common/PublishStatus";
import { Tooltip } from "../../../../components/common/tooltip";
import { Title } from "../../../../components/listViewTemplates";
import { PublishedStatusTypes, SortOptions } from "../../../../enums";
import { type ColumnOption } from "../../../../interfaces/columnOptions";
import dateTimeUtils from "../../../../utils/dateTimeUtils";
import { type EmbeddedVideoOverview } from "../types/models";
import { EmbeddedVideosThreeDotsItemMenu } from "./EmbeddedVideosThreeDotsItemMenu";
import cn from "classnames";

export enum Columns {
  Title = "Title",
  Publisher = "Publisher",
  Status = "Status",
  Added = "Added",
  Modified = "Modified",
}

export const columnToParamMap: {
  [P in Lowercase<Columns>]: string;
} = {
  title: "title",
  publisher: "publisher",
  status: "isDraft",
  modified: "dateModified",
  added: "dateCreated",
};

export const sortOptions = [
  {
    text: "Most Recent",
    value: SortOptions.CreatedDateDesc,
    default: true,
  },
  {
    text: "Last Modified",
    value: SortOptions.ModifiedDateDesc,
  },
  {
    text: "Sorted A-Z",
    value: SortOptions.Title,
  },
];

export const getColumnOptions = ({
  readonly,
  hideDisabled,
  handleEdit,
  handleDuplicate,
  handleDelete,
  handleToggleVisibility,
}: {
  readonly?: boolean;
  hideDisabled?: boolean;
  handleEdit: (id: number, isDraft: boolean) => void;
  handleDuplicate: (ids: number[]) => void;
  handleDelete: (ids: number[], flowsCount?: number, packsCount?: number) => void;
  handleToggleVisibility: (ids: number[], hidden: boolean) => () => void;
}): ColumnOption<EmbeddedVideoOverview>[] => {
  return [
    {
      name: Columns.Title,
      width: 4,
      isSortable: true,
      render: (video) => (
        <HiddenContent variant="list" isHidden={!video.visibility} showIcon>
          <Title
            url={video.id.toString()}
            title={video.title}
            clamp={2}
            className={cn("normal-color", { "hidden-content": !video.visibility })}
          />
        </HiddenContent>
      ),
    },
    {
      name: Columns.Publisher,
      width: 3,
      isSortable: false,
      render: (video) => (
        <Tooltip
          target={
            <TextTruncate>
              <HiddenContent variant="list" isHidden={!video.visibility}>
                {video.publisher}
              </HiddenContent>
            </TextTruncate>
          }
          content={video.publisher}
          className={cn({ "hidden-content": !video.visibility })}
        />
      ),
    },
    {
      name: Columns.Status,
      width: 3,
      isSortable: true,
      render: (video) => (
        <HiddenContent variant="list" isHidden={!video.visibility}>
          <PublishStatus
            publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(!video.isDraft, video.hasBeenPublished)}
          />
        </HiddenContent>
      ),
    },
    {
      name: Columns.Added,
      width: 2,
      isSortable: true,
      render: (video) => (
        <HiddenContent variant="list" isHidden={!video.visibility}>
          {dateTimeUtils.formatDate(video.dateCreated)}
        </HiddenContent>
      ),
    },
    {
      name: Columns.Modified,
      width: 2,
      isSortable: true,
      render: (video) => (
        <HiddenContent variant="list" isHidden={!video.visibility}>
          {dateTimeUtils.formatDate(video.dateModified)}
        </HiddenContent>
      ),
    },
    {
      name: "",
      width: 1,
      className: "align-right",
      isSortable: false,
      render: (video, isChecked) => {
        return (
          <EmbeddedVideosThreeDotsItemMenu
            circle
            outlinedEllipsis
            item={video}
            isChecked={isChecked}
            readonly={readonly}
            hasPermission={!readonly}
            hideDisabled={hideDisabled}
            handleEdit={handleEdit}
            handleDuplicate={handleDuplicate}
            handleDelete={handleDelete}
            handleToggleVisibility={handleToggleVisibility}
          />
        );
      },
    },
  ];
};
